// src/features/curatedLists/actions/curatedListsActions.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPatchRequest,
  sendPostRequest,
} from '../../../api/requestUtil';
import {
  setCurrentList,
  setError,
  setIsCreating,
  setIsFetchingMetadata,
  updatePendingItemMetadata,
} from '../slices/curatedListsSlice';
import {
  AddAppContentToListRequest,
  AddAppContentToListResponse,
  AddItemRequest,
  AppContentType,
  CreateListRequest,
  CuratedList,
  CuratedListSimple,
  ListItem,
  PaginatedCuratedLists,
  ToggleFeaturedRequest,
  UrlMetadata,
  validateAppContentType,
} from '../types/curatedLists.types';
import { extractErrorMessage } from '../utils/errorHandling';

// Type-safe response handler
const handleApiResponse = async <T,>(
  apiCall: () => Promise<any>,
  errorMessage: string
): Promise<T> => {
  try {
    const response = await apiCall();
    return response as T;
  } catch (error: any) {
    // Error is re-thrown and handled by calling action
    throw error;
  }
};

// ===== LIST CRUD ACTIONS =====

export const createList = createAsyncThunk<
  CuratedList,
  { listData: CreateListRequest; items?: any[] },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/createList',
  async ({ listData, items = [] }, { rejectWithValue, dispatch, getState }) => {
    try {
      dispatch(setIsCreating(true));

      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const requestData = {
        ...listData,
        description: listData.description || '',
        items: items.map(item => ({
          url: item.url,
          title: item.title || undefined,
          note: item.note || '',
        })),
      };

      const response = await handleApiResponse<CuratedList>(
        () => sendPostRequest('/api/curation/lists/', requestData, token),
        'Create list error'
      );

      if (!response.items) {
        response.items = [];
      }

      dispatch(setCurrentList(response));
      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to create list');
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setIsCreating(false));
    }
  }
);

export const getListByUsernameAndSlug = createAsyncThunk<
  CuratedList,
  { username: string; slug: string },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/getListByUsernameAndSlug',
  async ({ username, slug }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken; // Public endpoint, token optional

      const url = `/api/curation/${username}/curated-lists/${slug}/`;

      const response = await handleApiResponse<CuratedList>(
        () => sendGetRequest(url, token),
        'Get list by username and slug error'
      );

      if (!response.items) {
        response.items = [];
      }

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to load list');
      return rejectWithValue(message);
    }
  }
);

export const getListById = createAsyncThunk<
  CuratedList,
  { listId: number },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/getListById',
  async ({ listId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken; // Public endpoint, token optional

      const response = await handleApiResponse<CuratedList>(
        () => sendGetRequest(`/api/curation/lists/${listId}/`, token),
        'Get list by ID error'
      );

      dispatch(setCurrentList(response));
      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to fetch list');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchMyCuratedLists = createAsyncThunk<
  CuratedListSimple[],
  void,
  { rejectValue: string; state: AppState }
>(
  'curatedLists/fetchMyCuratedLists',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const response = await handleApiResponse<CuratedListSimple[]>(
        () => sendGetRequest('/api/curation/lists/me/', token),
        'Fetch my lists error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to fetch my lists');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== LIST ITEM ACTIONS =====

export const addItemToList = createAsyncThunk<
  ListItem,
  { listId: number; itemData: AddItemRequest },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/addItemToList',
  async ({ listId, itemData }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const requestData = {
        ...itemData,
        note: itemData.note || '',
      };

      const response = await handleApiResponse<ListItem>(
        () =>
          sendPostRequest(
            `/api/curation/lists/${listId}/items/`,
            requestData,
            token
          ),
        'Add item to list error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to add item');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const deleteList = createAsyncThunk<
  number,
  { listId: number },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/deleteList',
  async ({ listId }, { rejectWithValue, getState }) => {
    try {
      const token = getState().user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      await handleApiResponse<void>(
        () => sendDeleteRequest(`/api/curation/lists/${listId}/`, token),
        'Delete list error'
      );

      return listId;
    } catch (error: any) {
      // No setError: the shared error would replace the trail page with
      // "List Not Found". DeleteTrailDialog shows this message instead.
      return rejectWithValue(extractErrorMessage(error, 'Failed to delete trail'));
    }
  }
);

export const deleteItemFromList = createAsyncThunk<
  number,
  { itemId: number },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/deleteItemFromList',
  async ({ itemId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      await handleApiResponse<void>(
        () => sendDeleteRequest(`/api/curation/items/${itemId}/`, token),
        'Delete item error'
      );

      return itemId;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to delete item');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const reorderListItems = createAsyncThunk<
  void,
  { listId: number; itemIds: number[] },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/reorderListItems',
  async ({ listId, itemIds }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      await handleApiResponse<void>(
        () =>
          sendPostRequest(
            `/api/curation/lists/${listId}/reorder/`,
            { item_ids: itemIds },
            token
          ),
        'Reorder items error'
      );

      await dispatch(getListById({ listId }));
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to reorder items');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== METADATA ACTIONS =====

export const fetchUrlMetadata = createAsyncThunk<
  { tempId: number; metadata: UrlMetadata },
  { url: string; tempId: number },
  { rejectValue: string }
>(
  'curatedLists/fetchUrlMetadata',
  async ({ url, tempId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsFetchingMetadata(true));

      const urlWithParams = `/api/curation/metadata/?url=${encodeURIComponent(
        url
      )}`;

      const response = await handleApiResponse<UrlMetadata>(
        () => sendGetRequest(urlWithParams, null),
        'Fetch metadata error'
      );

      dispatch(
        updatePendingItemMetadata({
          tempId,
          metadata: response,
        })
      );

      return { tempId, metadata: response };
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to fetch metadata');
      return rejectWithValue(message);
    } finally {
      dispatch(setIsFetchingMetadata(false));
    }
  }
);

// ===== UPDATE ACTIONS =====

export const updateListMetadata = createAsyncThunk<
  void,
  { listId: number; title?: string; description?: string },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/updateListMetadata',
  async (
    { listId, title, description },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const updateData: { title?: string; description?: string } = {};
      if (title !== undefined) updateData.title = title;
      if (description !== undefined) updateData.description = description || '';

      await handleApiResponse<void>(
        () =>
          sendPatchRequest(`/api/curation/lists/${listId}/`, updateData, token),
        'Update list metadata error'
      );
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to update list');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const deleteMultipleItems = createAsyncThunk<
  number[],
  { itemIds: number[] },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/deleteMultipleItems',
  async ({ itemIds }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      await Promise.all(
        itemIds.map(itemId =>
          handleApiResponse<void>(
            () => sendDeleteRequest(`/api/curation/items/${itemId}/`, token),
            `Delete item ${itemId} error`
          )
        )
      );

      return itemIds;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to delete items');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const addMultipleItems = createAsyncThunk<
  ListItem[],
  { listId: number; items: AddItemRequest[] },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/addMultipleItems',
  async ({ listId, items }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const responses = await Promise.all(
        items.map(item =>
          handleApiResponse<ListItem>(
            () =>
              sendPostRequest(
                `/api/curation/lists/${listId}/items/`,
                {
                  url: item.url,
                  title: item.title || undefined,
                  note: item.note || '',
                },
                token
              ),
            `Add item error for ${item.url}`
          )
        )
      );

      return responses;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to add items');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const updateListItem = createAsyncThunk<
  ListItem,
  { itemId: number; note?: string; description?: string; title?: string },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/updateListItem',
  async ({ itemId, note, description, title }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const response = await handleApiResponse<ListItem>(
        () => sendPatchRequest(`/api/curation/items/${itemId}/`, { note, description, title }, token),
        'Update item error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to update item');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const updateList = createAsyncThunk<
  CuratedList,
  {
    listId: number;
    listData?: { title?: string; description?: string };
    itemsToAdd?: AddItemRequest[];
    itemIdsToDelete?: number[];
    reorderItemIds?: number[];
    itemsToUpdate?: Array<{ id: number; note: string; description: string }>;
  },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/updateList',
  async (
    { listId, listData, itemsToAdd = [], itemIdsToDelete = [], reorderItemIds, itemsToUpdate = [] },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setIsCreating(true));

      // 1. Update metadata if needed
      if (listData && (listData.title || listData.description !== undefined)) {
        await dispatch(
          updateListMetadata({
            listId,
            title: listData.title,
            description: listData.description,
          })
        ).unwrap();
      }

      // 2-4. Delete, update, and add items in parallel (all independent operations)
      await Promise.all([
        itemIdsToDelete.length > 0
          ? dispatch(deleteMultipleItems({ itemIds: itemIdsToDelete })).unwrap()
          : Promise.resolve(),
        itemsToUpdate.length > 0
          ? Promise.all(
              itemsToUpdate.map(({ id, note, description }) =>
                dispatch(updateListItem({ itemId: id, note, description })).unwrap()
              )
            )
          : Promise.resolve(),
        itemsToAdd.length > 0
          ? dispatch(addMultipleItems({ listId, items: itemsToAdd })).unwrap()
          : Promise.resolve(),
      ]);

      // 5. Reorder items if needed (must happen after adds complete)
      if (reorderItemIds && reorderItemIds.length > 0) {
        await dispatch(
          reorderListItems({ listId, itemIds: reorderItemIds })
        ).unwrap();
      }

      // 6. Fetch updated list
      const result = await dispatch(getListById({ listId })).unwrap();
      return result;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to update list');
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setIsCreating(false));
    }
  }
);

// ===== APP CONTENT ACTIONS =====

export const addAppContentToList = createAsyncThunk<
  AddAppContentToListResponse,
  AddAppContentToListRequest,
  { rejectValue: string; state: AppState }
>(
  'curatedLists/addAppContentToList',
  async (data, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      if (!validateAppContentType(data.app_content_type)) {
        return rejectWithValue(
          `Invalid content type: ${data.app_content_type}`
        );
      }

      const requestData = {
        ...data,
        note: data.note || '',
      };

      const response = await handleApiResponse<AddAppContentToListResponse>(
        () =>
          sendPostRequest('/api/curation/add-app-content/', requestData, token),
        'Add app content to list error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(
        error,
        'Failed to add to curated list'
      );
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const createListWithAppContent = createAsyncThunk<
  { list: CuratedList; item?: AddAppContentToListResponse },
  {
    title: string;
    description?: string;
    app_content_type: AppContentType;
    app_content_id: number;
    note?: string;
  },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/createListWithAppContent',
  async (data, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      // Step 1: Create the list
      const listData = {
        title: data.title,
        description:
          data.description || `Collection of ${data.app_content_type}s`,
      };

      const listResponse = await handleApiResponse<CuratedList>(
        () => sendPostRequest('/api/curation/lists/', listData, token),
        'Create list error'
      );

      // Step 2: Add the content to the new list
      const addContentData: AddAppContentToListRequest = {
        app_content_type: data.app_content_type,
        app_content_id: data.app_content_id,
        list_id: listResponse.id,
        note: data.note || '',
      };

      const itemResponse = await handleApiResponse<AddAppContentToListResponse>(
        () =>
          sendPostRequest(
            '/api/curation/add-app-content/',
            addContentData,
            token
          ),
        'Add content to new list error'
      );

      return { list: listResponse, item: itemResponse };
    } catch (error: any) {
      const message = extractErrorMessage(
        error,
        'Failed to create list with content'
      );
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== BOOKMARK ACTIONS =====

export const fetchUserBookmarkedLists = createAsyncThunk<
  CuratedListSimple[],
  void,
  { rejectValue: string; state: AppState }
>(
  'curatedLists/fetchUserBookmarkedLists',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const response = await handleApiResponse<CuratedListSimple[]>(
        () => sendGetRequest('/api/curation/bookmarks/', token),
        'Fetch bookmarked lists error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(
        error,
        'Failed to fetch bookmarked lists'
      );
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const toggleListBookmark = createAsyncThunk<
  { is_bookmarked: boolean; total_bookmarks: number },
  { listId: number },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/toggleListBookmark',
  async ({ listId }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const response = await handleApiResponse<{
        is_bookmarked: boolean;
        total_bookmarks: number;
      }>(
        () =>
          sendPostRequest(
            '/api/curation/bookmarks/toggle/',
            { list_id: listId },
            token
          ),
        'Toggle bookmark error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to toggle bookmark');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== HELPER ACTIONS =====

export const reorderPendingItemsAction = (itemIds: string[]) => {
  return (dispatch: any) => {
    dispatch({
      type: 'curatedLists/reorderPendingItems',
      payload: itemIds,
    });
  };
};


// ===== PAGINATED LIST ACTIONS =====


export const fetchPaginatedCuratedLists = createAsyncThunk<
  PaginatedCuratedLists,
  { page: number; pageSize?: number; searchQuery?: string; ordering?: string },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/fetchPaginatedCuratedLists',
  async ({ page, pageSize = 12, searchQuery, ordering = '-created_at' }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken; // Optional for public endpoint

      // Build URL with query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      params.append('ordering', ordering);
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const url = `/api/curation/lists?${params.toString()}`;

      const response = await handleApiResponse<PaginatedCuratedLists>(
        () => sendGetRequest(url, token),
        'Fetch paginated lists error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to load lists');
      return rejectWithValue(message);
    }
  }
);

export const fetchAllCuratedLists = createAsyncThunk<
  CuratedList[],
  void,
  { rejectValue: string; state: AppState }
>(
  'curatedLists/fetchAllCuratedLists',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken; // Optional for public endpoint

      const url = '/api/curation/lists?page_size=100';

      const response = await handleApiResponse<PaginatedCuratedLists>(
        () => sendGetRequest(url, token),
        'Fetch all lists error'
      );

      return response.items || [];
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to load all lists');
      return rejectWithValue(message);
    }
  }
);


export const toggleListFeatured = createAsyncThunk<
  CuratedList,
  { listId: number; isFeatured: boolean },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/toggleListFeatured',
  async ({ listId, isFeatured }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      if (!token) {
        return rejectWithValue('Authentication required');
      }

      const requestData: ToggleFeaturedRequest = {
        list_id: listId,
        is_featured: isFeatured,
      };

      const response = await handleApiResponse<CuratedList>( 
        () =>
          sendPostRequest(
            '/api/curation/lists/featured/toggle/',
            requestData,
            token
          ),
        'Toggle featured status error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to toggle featured status');
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);


export const fetchFeaturedLists = createAsyncThunk<
  CuratedList[],
  void,
  { rejectValue: string; state: AppState }
>(
  'curatedLists/fetchFeaturedLists',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken; // Optional for public endpoint

      const response = await handleApiResponse<CuratedList[]>(
        () => sendGetRequest('/api/curation/lists/featured/', token),
        'Fetch featured lists error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to fetch featured lists');
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch paginated featured lists (for a "Featured" section with more)
 */
export const fetchPaginatedFeaturedLists = createAsyncThunk<
  PaginatedCuratedLists,
  { page: number; pageSize?: number },
  { rejectValue: string; state: AppState }
>(
  'curatedLists/fetchPaginatedFeaturedLists',
  async ({ page, pageSize = 12 }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.user.authToken;

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      const url = `/api/curation/lists/featured/all/?${params.toString()}`;

      const response = await handleApiResponse<PaginatedCuratedLists>(
        () => sendGetRequest(url, token),
        'Fetch paginated featured lists error'
      );

      return response;
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to fetch featured lists');
      return rejectWithValue(message);
    }
  }
);
